import { format } from "date-fns";
import { JobStatus } from "../../../lib/jobStatus.utils";
import { isMultiplePartOrdersJob } from "../../../hooks/useMultiplePartOrders";
import { InspectionRowLabel, inspectionRows } from "../inspectionRows.utils";
import { US_STATES } from "../../PreJobChecklistV2/constants";
import { parseMiles, parseStoredMiles } from "../formatMiles.utils";
import { EjiItem, EjiService, Job, PreJobCheckListV2, VehicleInfo, VehicleType } from "../../../generated/nest-graphql";

/** VEHICLE-HISTORY (NUB-358): the rules, as plain functions — no React, no Apollo. */

/**
 * `Closed` is the only terminal status and the only one `closeJob` writes, so it is
 * what "completed" means here. When business defines it further, change this.
 */
export const isCompletedVisit = (job?: Job | null): boolean => job?.status === JobStatus.CLOSED;

const normaliseVin = (vin?: string | null) => (vin ?? "").trim().toUpperCase();

/** Plates are typed by humans on both sides, so punctuation and spacing cannot be trusted. */
const normalisePlate = (plate?: string | null) => (plate ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

/**
 * `vehicleId` is `required: false`, so older jobs carry only the embedded `vehicleInfo`, and one car can
 * also exist as two vehicle records. The VIN identifies the car, so a VIN match counts across records;
 * different records only stay apart when there is no VIN to tie them, and plate is the last resort.
 */
export const jobMatchesVehicle = (job?: Job | null, vehicle?: VehicleType | null): boolean => {
  if (!job || !vehicle) return false;

  if (job.vehicleId && vehicle.id && String(job.vehicleId) === String(vehicle.id)) return true;

  const jobVin = normaliseVin(job.vehicleInfo?.vin);
  const vehicleVin = normaliseVin(vehicle.vin);
  if (jobVin && vehicleVin) return jobVin === vehicleVin;
  if (job.vehicleId && vehicle.id) return false;

  const jobPlate = normalisePlate(job.vehicleInfo?.licensePlate);
  const vehiclePlate = normalisePlate(vehicle.licensePlate);
  if (!jobPlate || !vehiclePlate || jobPlate !== vehiclePlate) return false;

  // Plates are reissued across states, so a plate match only counts within one state.
  const jobState = (job.vehicleInfo?.licensePlateState ?? "").trim().toUpperCase();
  const vehicleState = (vehicle.licensePlateState ?? "").trim().toUpperCase();
  return !jobState || !vehicleState || jobState === vehicleState;
};

/**
 * The invoice date is the closest thing to "the day this was serviced"; the
 * appointment covers visits that never produced one, `createdAt` only stops undated
 * jobs sorting randomly.
 */
export const serviceDateOf = (job?: Job | null): Date | null => {
  const raw = job?.invoice?.issuedDate ?? job?.appointment?.startDate ?? job?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `getJobs` sorts by `_id` — newest created, which rescheduling makes the wrong order. */
export const byServiceDateDesc = (a: Job, b: Job): number => {
  const left = serviceDateOf(a)?.getTime() ?? 0;
  const right = serviceDateOf(b)?.getTime() ?? 0;
  return right - left;
};

export { formatMiles, parseMiles, parseStoredMiles } from "../formatMiles.utils";

export const formatServiceDate = (date?: Date | null): string => (date ? format(date, "d MMM yyyy") : "No date");

const BRAKE_WORK = /\b(brakes?|pads?|rotors?|calipers?|drums?|shoes?)\b/i;

/** Carfax entries for brake work, so the tech can spot them in another shop's history. */
export const isBrakeWork = (text?: string | null): boolean => !!text && BRAKE_WORK.test(text);

/**
 * Carfax sends its dates as display text, usually MM/DD/YYYY; shown like the NuBrakes ones when they parse,
 * as sent otherwise.
 */
const parseCarfaxDate = (value: string): Date | null => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (!match) return null;
  const [, month, day, year] = match.map(Number);
  const date = new Date(year, month - 1, day);
  const valid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return valid ? date : null;
};

export const formatCarfaxDate = (displayDate?: string | null): string | null => {
  const value = displayDate?.trim();
  if (!value) return null;
  const date = parseCarfaxDate(value);
  return date ? formatServiceDate(date) : value;
};

// Unreadable dates count as oldest; ties keep the list's order.
export const latestBrakeRecordIndex = (records: { displayDate?: string | null; lines: string[] }[]): number => {
  let best = -1;
  let bestTime = -Infinity;
  records.forEach((record, index) => {
    if (!record.lines.some(isBrakeWork)) return;
    const time = parseCarfaxDate(record.displayDate?.trim() ?? "")?.getTime() ?? -Infinity;
    if (best === -1 || time > bestTime) {
      best = index;
      bestTime = time;
    }
  });
  return best;
};

export type CarfaxMileageCheck = {
  carfaxMiles: number;
  /** The record's displayDate as Carfax sent it. */
  carfaxDate: string | null;
  storedMiles: number | null;
  /** Null when no mileage is on record. */
  difference: number | null;
};

/** Carfax's highest reading, when it is above the stored mileage or there is none. */
export const carfaxMileageCheck = (
  storedOdometer: string | null | undefined,
  records: Array<{ odometer?: string | null; displayDate?: string | null } | null | undefined>
): CarfaxMileageCheck | null => {
  let best: { miles: number; date: string | null; time: number } | null = null;
  for (const record of records) {
    const miles = parseMiles(record?.odometer);
    if (miles === null) continue;
    const date = record?.displayDate?.trim() || null;
    const time = parseCarfaxDate(date ?? "")?.getTime() ?? -Infinity;
    if (!best || miles > best.miles || (miles === best.miles && time > best.time)) best = { miles, date, time };
  }
  if (!best) return null;
  const storedMiles = parseStoredMiles(storedOdometer);
  // An unreadable stored mileage (e.g. "~95k") might be higher, so never offer to lower it.
  if (storedMiles === null && /[1-9]/.test(storedOdometer ?? "")) return null;
  if (storedMiles !== null && best.miles <= storedMiles) return null;
  return {
    carfaxMiles: best.miles,
    carfaxDate: best.date,
    storedMiles,
    difference: storedMiles === null ? null : best.miles - storedMiles,
  };
};

export type InvoicedPart = {
  quantity: number | null;
  name: string;
  partNumber: string | null;
  /** False when the part's order names no parts store, which the job page shows as "No Parts Store". */
  hasPartsStore: boolean;
};
export type InvoicedService = { name: string; parts: InvoicedPart[] };

export type ServiceVisit = {
  key: string;
  job: Job;
  odometer: string | null;
  services: InvoicedService[];
};

/**
 * Parts only, read as the job page reads them: a job created after the part-order cutover keeps its number and store
 * on `jobItem` (its part order's store), an older one on the legacy `orderItem`.
 */
const partsOf = (service: EjiService, newerPartOrders: boolean): InvoicedPart[] =>
  ((service.items ?? []).filter((item) => item?.category === "Part") as EjiItem[]).map((item) => {
    const order = newerPartOrders
      ? { partNumber: item.jobItem?.partNumber, store: item.jobItem?.partOrder?.store }
      : { partNumber: item.orderItem?.partNumber, store: item.orderItem?.partsStore };
    return {
      quantity: item.units ?? null,
      name: item.productSelection?.name?.trim() || "Part",
      partNumber: order.partNumber?.trim() || null,
      hasPartsStore: !!(order.store?.vendor && order.store?.name),
    };
  });

/** One card per visit, newest first as given, with what its invoice says was done (possibly nothing). */
export const toServiceVisits = (jobs: Job[]): ServiceVisit[] =>
  jobs.map((job) => ({
    key: job.id ?? `${job.jobNumber}`,
    job,
    odometer: job.preJobCheckListV2?.odometer || job.vehicleInfo?.odometer || null,
    services: ((job?.invoice?.services ?? []).filter(Boolean) as EjiService[]).map((service) => ({
      name: service.name,
      parts: partsOf(service, isMultiplePartOrdersJob(job.createdAt)),
    })),
  }));

/** How a car is named wherever the history names one: year, make and model. */
export const describeVehicle = (
  vehicle?: { year?: string | null; make?: string | null; model?: string | null } | null
): string | null => [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ") || null;

/**
 * A checklist can exist with nothing filled in, when an inspection was opened and never done. Uses the
 * summary's own rows, except the odometer, which the form pre-fills from the vehicle.
 */
export const hasRecordedInspection = (checklist?: PreJobCheckListV2 | null): boolean =>
  !!checklist &&
  (inspectionRows(checklist).some((row) => row.label !== InspectionRowLabel.ODOMETER && !!row.value) ||
    !!checklist.privateNotes?.trim());

/** Jobs without a `vehicleId` still carry the embedded info, which is enough to match by VIN or plate. */
export const vehicleFromJobInfo = (info?: VehicleInfo | null): VehicleType | null => {
  if (!info?.vin && !info?.licensePlate) return null;
  return {
    year: info.year,
    make: info.make,
    model: info.model,
    vin: info.vin,
    licensePlate: info.licensePlate,
    licensePlateState: info.licensePlateState,
  } as VehicleType;
};

// Ops and the inspection save a state's name ("Texas"), the fleet portal its code; show the code either way.
export const stateCode = (state?: string | null): string | null => {
  const text = state?.trim();
  if (!text) return null;
  return US_STATES.find((option) => option.value.toLowerCase() === text.toLowerCase())?.label ?? text;
};
